
import { getBfixJobs } from '@/api';
import JobBfixTable from '@/components/dashBoard/jobBfixTable/JobBfixTable.vue';
import ReloadButton from '@/components/dashBoard/reloadButton/ReloadButton.vue';
import SelectFields from '@/components/dashBoard/selectsFields/SelectFields.vue';
import StatusButtons from '@/components/dashBoard/statusButtons/StatusButtons.vue';
import Notification from '@/components/UI/Notification.vue';
import Chip from '@/components/UI/chip/Chip.vue';
import { IGetJobs, IJobs, Job, JobBfixData, JobsInLocation, User } from '@/models/main';
import Vue from 'vue';
import store from '@/store';
import { Component, Watch } from 'vue-property-decorator';
import { mapState } from 'vuex';
import { Action } from 'vuex-class';
import { format, utcToZonedTime } from 'date-fns-tz';
import { addDays } from 'date-fns';
import { timeZoneDefault } from '@/utils/PortalConstants';

@Component({
  components: {
    Chip,
    JobBfixTable,
    StatusButtons,
    ReloadButton,
    SelectFields,
    Notification,
  },
  computed: {
    ...mapState([
      'jobData',
      'jobBfixData',
      'availableStates',
      'alertTimeout',
      'storeSearch',
      'storeShowBfixCategory',
      'storeSelectedStates',
      'storeDrivers',
    ]),
    ...mapState('moduleUser', { userSelectedDates: 'userSelectedDates' }),
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class Dashboard extends Vue {
  private notification = false;
  private notificationClose = true;
  private notificationColor = 'red';
  private notificationText = '';
  private notificationTimeout = 5000;

  private loading = false;
  private timeout: number;
  private interval: number;
  private today: string = format(new Date(), 'yyyy-MM-dd');
  private storeSearch!: string;
  private storeSelectedStates!: string[];
  private alertTimeout!: number;
  private storeDrivers!: Array<string | number>;
  private storeShowBfixCategory!: string;
  private availableStates!: string[];
  private selectDates!: string[];
  private userSelectedDates!: string[];
  private jobData!: IJobs;
  private user!: User;

  @Action
  private setJobBfixData!: (jobs: IGetJobs) => void;

  private jobBfixData: JobBfixData;
  private filteredJobs: JobsInLocation[];

  private isFiltersSet = false;

  private clickChip(): void {
    this.isFiltersSet = !this.isFiltersSet;
  }

  private jobsCategoryCount(category): number {
    let jobsCounter = 0;
    this.jobBfixData[category].map((location: JobsInLocation) => {
      Object.values(location).map(locationJobs => {
        jobsCounter += locationJobs.length;
      });
    });
    return jobsCounter;
  }

  private timeoutAlert(): void {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.notification = true;
      this.notificationTimeout = -1;
      this.notificationText = 'The data is outdated. Click refresh button, please';
    }, 5 * 60 * 1000) as unknown as number; // 5min
  }

  private dataSync(): void {
    this.loading = true;
    if (this.selectDates?.length) {
      getBfixJobs(this.selectDates)
        .then((data: IGetJobs) => {
          this.setJobBfixData(data);
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  }

  private get showEdit(): boolean {
    const userTimeZone = this.user.timezone || timeZoneDefault;
    const today = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
    return this.selectDates?.length === 1 && this.selectDates[0] === today;
  }

  private filteredItems(category: string): JobsInLocation[] {
    let filteredJobs: JobsInLocation[];
    // Filtered for category
    switch (category) {
      case 'all':
        filteredJobs = this.jobBfixData.all;
        break;
      case 'unallocated':
        filteredJobs = this.jobBfixData.unallocated;
        break;
      case 'progress':
        filteredJobs = this.jobBfixData.in_progress;
        break;
      case 'completed':
        filteredJobs = this.jobBfixData.completed;
        break;
      case 'cancelled':
        filteredJobs = this.jobBfixData.cancelled;
        break;
      default:
        filteredJobs = [];
    }

    // Filtered for select state
    if (this.storeSelectedStates.length !== this.availableStates.length) {
      const newFilteredJobs: JobsInLocation[] = [];
      filteredJobs.map(location => {
        Object.values(location).map(jobs => {
          const newLocationArray: Job[] = [];
          jobs.map((job: Job) => {
            if (
              (job.main_stop === 1 && this.storeSelectedStates.includes(job.suburb_state1)) ||
              (job.main_stop === 2 && this.storeSelectedStates.includes(job.suburb_state2))
            ) {
              newLocationArray.push(job);
            }
          });
          if (newLocationArray.length >= 1) {
            const filtredJobsObject = {
              [Object.keys(location)[0]]: newLocationArray,
            };
            newFilteredJobs.push(filtredJobsObject);
          }
        });
      });
      filteredJobs = newFilteredJobs;
    }

    // Filtered for search input
    if (this.storeSearch) {
      const search = this.storeSearch.toUpperCase();
      const newFilteredJobs: JobsInLocation[] = [];
      filteredJobs.map(location => {
        Object.values(location).map(jobs => {
          const newLocationArray: Job[] = [];
          jobs.map((job: Job) => {
            if (
              job.id.toString().includes(search) ||
              job.customer_code.includes(search) ||
              job.customer_code.includes(search) ||
              job.stop1_name.includes(search) ||
              job.stop2_name.includes(search) ||
              (job.main_stop === 1 && job.suburb_name1.toUpperCase().includes(search)) ||
              (job.main_stop === 1 && job.suburb_state1.toUpperCase().includes(search)) ||
              (job.main_stop === 1 && job.driver1.toString().includes(search)) ||
              (job.main_stop === 2 && job.suburb_name2.toUpperCase().includes(search)) ||
              (job.main_stop === 2 && job.suburb_state2.toUpperCase().includes(search)) ||
              (job.main_stop === 2 && job.driver2.toString().includes(search))
            ) {
              newLocationArray.push(job);
            }
          });
          if (newLocationArray.length >= 1) {
            const filtredJobsObject = {
              [Object.keys(location)[0]]: newLocationArray,
            };
            newFilteredJobs.push(filtredJobsObject);
          }
        });
      });
      filteredJobs = newFilteredJobs;
    }

    // Filtered for select drivers
    if (this.storeDrivers.length > 0) {
      const newFilteredJobs: JobsInLocation[] = [];
      filteredJobs.map(location => {
        Object.values(location).map(jobs => {
          const newLocationArray: Job[] = [];
          jobs.map((job: Job) => {
            if (this.storeDrivers.includes(job.driver1) || this.storeDrivers.includes(job.driver2)) {
              newLocationArray.push(job);
            }
          });
          if (newLocationArray.length >= 1) {
            const filtredJobsObject = {
              [Object.keys(location)[0]]: newLocationArray,
            };
            newFilteredJobs.push(filtredJobsObject);
          }
        });
      });
      filteredJobs = newFilteredJobs;
    }

    if (this.isFiltersSet) {
      const newFilteredJobs: JobsInLocation[] = [];
      filteredJobs.map(location => {
        Object.values(location).map(jobs => {
          const newLocationArray: Job[] = [];
          jobs.map((job: Job) => {
            if (!job.can_schedule) {
              newLocationArray.push(job);
            }
          });
          if (newLocationArray.length >= 1) {
            const filtredJobsObject = {
              [Object.keys(location)[0]]: newLocationArray,
            };
            newFilteredJobs.push(filtredJobsObject);
          }
        });
      });
      filteredJobs = newFilteredJobs;
    }

    this.filteredJobs = filteredJobs;
    return filteredJobs;
  }

  private timeCycle(): void {
    // update interval 1 min
    this.interval = setInterval(() => {
      this.dataSync();
    }, 5 * 60 * 1000) as unknown as number;
  }

  @Watch('$route.path', { immediate: true })
  private changeRoute(): void {
    if (this.$route.path === '/dash-bfix') {
      if (this.jobData.all.length === 0) {
        this.dataSync();
      } else {
        this.$nextTick(() => {
          this.notification = true;
          this.notificationTimeout = -1;
          this.notificationText = 'The data is outdated. Click refresh button, please';
        });
      }
    }
  }

  private clickNotification(): void {
    this.notification = false;
    if (this.notificationText === 'The data is outdated. Click refresh button, please') {
      this.dataSync();
    }
  }

  private beforeDestroy(): void {
    clearTimeout(this.timeout);
    clearInterval(this.interval);
  }

  private changeDate(dates): void {
    this.selectDates = dates;
    store.dispatch('moduleUser/setUserDates', dates);
    this.dataSync();
  }

  private mounted(): void {
    this.selectDates = [];
    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.today = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
    if (this.userSelectedDates.length === 2) {
      this.selectDates = [...this.userSelectedDates];
    } else {
      const dayAfterOneWeek = format(utcToZonedTime(addDays(new Date(), 6), userTimeZone), 'yyyy-MM-dd');
      this.selectDates = [this.today, dayAfterOneWeek];
      store.dispatch('moduleUser/setUserDates', this.selectDates);
    }
    this.dataSync();
  }
}
